import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import {
  CreateUserInput,
  IPersonUserForm,
} from "../components/routeComponents/Persons/Person";
import GatewayService from "../services/GatewayService";

export enum UserActions {
  CREATE = "create",
}

const useUserMutation = () => {
  const create = useMutation(
    (req: CreateUserInput) => GatewayService.createUserWithPerson(req),
    ResponseHandling("person_user_created")
  );

  const mutate = async (action: UserActions, input: IPersonUserForm) => {
    switch (action) {
      case UserActions.CREATE:
        const createUser = mapFormToCreate(input);
        return await create.mutateAsync(createUser);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: create.isLoading,
      create: {
        isSuccess: create.isSuccess,
      },
    },
  };
};

export default useUserMutation;

function mapFormToCreate(input: IPersonUserForm): CreateUserInput {
  const contractingCompany =
    typeof input.contractingCompany === "string"
      ? input.contractingCompany
      : input.contractingCompany?.id;

  const relationshipToCustomer =
    typeof input.relationshipToCustomer === "string"
      ? input.relationshipToCustomer
      : input.relationshipToCustomer?.id;

  return {
    username: input.username,
    customerId: UsersService.customer?.id,
    contractingCompany,
    relationshipToCustomer,
    displayName: input?.displayName,
    email: input?.email,
    firstName: input?.firstName,
    lastName: input?.lastName,
  };
}

function ResponseHandling(
  messageKey: string = "person_create_success_message"
) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}
